import { useEffect, useRef, useState } from 'react';
import type { GeoBoundingBox, VideoTrackExtent } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';
import { useT } from '../../i18n/index.js';

/**
 * Sélecteur d'emprise géographique **souverain** : un canevas sans fond cartographique externe (pas
 * de tuiles OSM/Mapbox — aucune fuite de localisation ni dépendance réseau, cohérent avec une
 * plateforme auto-hébergeable). On y situe les traces de vol de l'organisation comme repère, et
 * l'utilisateur dessine un rectangle à la souris/au doigt pour définir sa zone de recherche (FR-21).
 *
 * Projection équirectangulaire (lon↔x, lat↔y) : suffisante pour un sélecteur, sans prétention
 * cartographique. L'emprise émise est `[ouest, sud, est, nord]` en degrés WGS 84.
 */
interface View {
  lonMin: number;
  lonMax: number;
  latMin: number;
  latMax: number;
}

const HEIGHT = 300;
const PAD = 12; // marge interne pour les étiquettes

/** Vue par défaut (monde) quand l'organisation n'a encore aucune trace géolocalisée. */
const WORLD: View = { lonMin: -180, lonMax: 180, latMin: -82, latMax: 82 };

export function GeoBoundingBoxPicker({
  value,
  onChange,
}: {
  value: GeoBoundingBox | null;
  onChange: (bbox: GeoBoundingBox | null) => void;
}) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [extents, setExtents] = useState<VideoTrackExtent[]>([]);
  const [view, setView] = useState<View>(WORLD);
  const [size, setSize] = useState({ width: 600, height: HEIGHT });
  // Rectangle en cours de tracé, en coordonnées canevas logiques (null hors tracé).
  const dragRef = useRef<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  // Le rectangle vit dans un ref (pas de re-rendu par déplacement) ; ce compteur force le redessin.
  const [drawTick, setDrawTick] = useState(0);

  // Repère : les emprises de vol de l'organisation (cloisonnées côté API).
  useEffect(() => {
    apiFetch<VideoTrackExtent[]>('/videos/track-extents')
      .then(setExtents)
      .catch(() => setExtents([]));
  }, []);

  // Cadre la vue sur l'ensemble des traces (avec marge), sinon le monde.
  useEffect(() => {
    if (extents.length === 0) {
      setView(WORLD);
      return;
    }
    let lonMin = Infinity;
    let lonMax = -Infinity;
    let latMin = Infinity;
    let latMax = -Infinity;
    for (const e of extents) {
      lonMin = Math.min(lonMin, e.minLon);
      lonMax = Math.max(lonMax, e.maxLon);
      latMin = Math.min(latMin, e.minLat);
      latMax = Math.max(latMax, e.maxLat);
    }
    // Marge de 15 %, avec un minimum pour éviter une vue dégénérée sur un vol très local.
    const lonSpan = Math.max(lonMax - lonMin, 0.01);
    const latSpan = Math.max(latMax - latMin, 0.01);
    setView({
      lonMin: clampLon(lonMin - lonSpan * 0.15),
      lonMax: clampLon(lonMax + lonSpan * 0.15),
      latMin: clampLat(latMin - latSpan * 0.15),
      latMax: clampLat(latMax + latSpan * 0.15),
    });
  }, [extents]);

  // Largeur réactive au conteneur (hauteur fixe).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver((entries) => {
      const width = Math.max(240, Math.round(entries[0]?.contentRect.width ?? 600));
      setSize({ width, height: HEIGHT });
    });
    observer.observe(canvas.parentElement ?? canvas);
    return () => observer.disconnect();
  }, []);

  const project = (lon: number, lat: number) => {
    const w = size.width - PAD * 2;
    const h = size.height - PAD * 2;
    return {
      x: PAD + ((lon - view.lonMin) / (view.lonMax - view.lonMin)) * w,
      y: PAD + ((view.latMax - lat) / (view.latMax - view.latMin)) * h,
    };
  };
  const unproject = (x: number, y: number) => {
    const w = size.width - PAD * 2;
    const h = size.height - PAD * 2;
    return {
      lon: view.lonMin + ((x - PAD) / w) * (view.lonMax - view.lonMin),
      lat: view.latMax - ((y - PAD) / h) * (view.latMax - view.latMin),
    };
  };

  // Rendu.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.width * dpr;
    canvas.height = size.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const css = getComputedStyle(canvas);
    const ground = css.getPropertyValue('--ddp-geo-ground').trim() || '#0b1220';
    const grid = css.getPropertyValue('--ddp-geo-grid').trim() || '#2a3550';
    const trackColor = css.getPropertyValue('--ddp-geo-track').trim() || '#5b7fb5';
    const accent = css.getPropertyValue('--ddp-geo-accent').trim() || '#4589ff';
    const label = css.getPropertyValue('--ddp-geo-label').trim() || '#8a94ad';

    ctx.clearRect(0, 0, size.width, size.height);
    ctx.fillStyle = ground;
    ctx.fillRect(0, 0, size.width, size.height);

    // Graticule : ~5 méridiens et ~5 parallèles « ronds » dans la vue.
    ctx.strokeStyle = grid;
    ctx.fillStyle = label;
    ctx.lineWidth = 1;
    ctx.font = '10px system-ui, sans-serif';
    for (const lon of ticks(view.lonMin, view.lonMax)) {
      const { x } = project(lon, view.latMax);
      ctx.beginPath();
      ctx.moveTo(x, PAD);
      ctx.lineTo(x, size.height - PAD);
      ctx.stroke();
      ctx.fillText(`${lon.toFixed(1)}°`, x + 2, size.height - 2);
    }
    for (const lat of ticks(view.latMin, view.latMax)) {
      const { y } = project(view.lonMin, lat);
      ctx.beginPath();
      ctx.moveTo(PAD, y);
      ctx.lineTo(size.width - PAD, y);
      ctx.stroke();
      ctx.fillText(`${lat.toFixed(1)}°`, 2, y - 2);
    }

    // Traces de vol : emprises en repère.
    ctx.strokeStyle = trackColor;
    ctx.fillStyle = withAlpha(trackColor, 0.18);
    for (const e of extents) {
      const a = project(e.minLon, e.maxLat);
      const b = project(e.maxLon, e.minLat);
      const w = Math.max(3, b.x - a.x);
      const h = Math.max(3, b.y - a.y);
      ctx.fillRect(a.x, a.y, w, h);
      ctx.strokeRect(a.x, a.y, w, h);
    }

    // Sélection : tracé en cours, sinon la valeur courante.
    const rect = dragRef.current
      ? normalizeRect(dragRef.current)
      : value
        ? rectFromBbox(value)
        : null;
    if (rect) {
      ctx.strokeStyle = accent;
      ctx.fillStyle = withAlpha(accent, 0.2);
      ctx.lineWidth = 1.5;
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    }

    function rectFromBbox([w, s, e, n]: GeoBoundingBox) {
      const a = project(w, n);
      const b = project(e, s);
      return { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extents, view, size, value, drawTick]);

  // Pointeur : tracé du rectangle.
  const toLocal = (event: React.PointerEvent) => {
    const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * size.width,
      y: ((event.clientY - rect.top) / rect.height) * size.height,
    };
  };

  const onPointerDown = (event: React.PointerEvent) => {
    (event.target as HTMLCanvasElement).setPointerCapture(event.pointerId);
    const p = toLocal(event);
    dragRef.current = { x0: p.x, y0: p.y, x1: p.x, y1: p.y };
    setDrawTick((n) => n + 1);
  };
  const onPointerMove = (event: React.PointerEvent) => {
    if (!dragRef.current) return;
    const p = toLocal(event);
    dragRef.current = { ...dragRef.current, x1: p.x, y1: p.y };
    setDrawTick((n) => n + 1);
  };
  const onPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    // Un simple clic (rectangle minuscule) efface plutôt que de filtrer sur un point.
    if (Math.abs(d.x1 - d.x0) < 4 || Math.abs(d.y1 - d.y0) < 4) {
      onChange(null);
      return;
    }
    const a = unproject(Math.min(d.x0, d.x1), Math.min(d.y0, d.y1)); // coin haut-gauche → nord-ouest
    const b = unproject(Math.max(d.x0, d.x1), Math.max(d.y0, d.y1)); // coin bas-droite → sud-est
    onChange([
      clampLon(Math.min(a.lon, b.lon)),
      clampLat(Math.min(a.lat, b.lat)),
      clampLon(Math.max(a.lon, b.lon)),
      clampLat(Math.max(a.lat, b.lat)),
    ]);
  };

  return (
    <div className="ddp-geo">
      <canvas
        ref={canvasRef}
        className="ddp-geo__canvas"
        style={{ width: '100%', height: `${HEIGHT}px`, touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        role="img"
        aria-label={t('search.geo.canvasLabel')}
      />
      <div className="ddp-geo__footer">
        {value ? (
          <span className="ddp-geo__readout">
            O {value[0].toFixed(3)}° · S {value[1].toFixed(3)}° · E {value[2].toFixed(3)}° · N{' '}
            {value[3].toFixed(3)}°
          </span>
        ) : (
          <span className="ddp-geo__hint">
            {extents.length === 0 ? t('search.geo.emptyHint') : t('search.geo.hint')}
          </span>
        )}
        {value && (
          <button type="button" className="ddp-geo__clear" onClick={() => onChange(null)}>
            {t('search.geo.clear')}
          </button>
        )}
      </div>
    </div>
  );

  function normalizeRect(d: { x0: number; y0: number; x1: number; y1: number }) {
    return {
      x: Math.min(d.x0, d.x1),
      y: Math.min(d.y0, d.y1),
      w: Math.abs(d.x1 - d.x0),
      h: Math.abs(d.y1 - d.y0),
    };
  }
}

/** ~5 graduations « rondes » entre deux bornes. */
function ticks(min: number, max: number): number[] {
  const span = max - min;
  if (span <= 0) return [min];
  const raw = span / 5;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max; v += step) out.push(Number(v.toFixed(6)));
  return out;
}

function clampLon(lon: number): number {
  return Math.max(-180, Math.min(180, lon));
}
function clampLat(lat: number): number {
  return Math.max(-90, Math.min(90, lat));
}

/** Ajoute un alpha à une couleur hex `#rrggbb` ; renvoie telle quelle si le format n'est pas reconnu. */
function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m?.[1]) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
